import { emit, on, session } from '@lucid/core';
import { lockScroll, unlockScroll } from '@theme/utilities';

const API = 'https://a.klaviyo.com/client';
const REVISION = '2026-07-15';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DONE_KEY = 'lucid:bis:done';

let active = null;

const sized = (url, width = 160) => {
  if (!url) return '';
  const absolute = url.startsWith('//') ? `https:${url}` : url;
  try {
    const parsed = new URL(absolute, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.toString();
  } catch {
    return absolute;
  }
};

const decode = (text) => new DOMParser().parseFromString(`<!doctype html><body>${text}`, 'text/html').body.textContent || '';

const readJSON = (element) => {
  try {
    return JSON.parse(element?.textContent || 'null');
  } catch {
    return null;
  }
};

function pageProduct({ productId, variantId, handle }) {
  for (const script of document.querySelectorAll('script[data-lucid-bis-product]')) {
    const product = readJSON(script);
    if (!product) continue;
    if (productId && String(product.id) === String(productId)) return product;
    if (handle && product.handle === handle) return product;
    if (variantId && product.variants?.some((variant) => String(variant.id) === String(variantId))) return product;
  }
  return null;
}

function fromAjax(data) {
  const variants = (data.variants || []).map((variant) => ({
    id: variant.id,
    title: variant.title,
    available: Boolean(variant.available),
    image: variant.featured_image?.src ? sized(variant.featured_image.src) : '',
  }));
  return {
    id: data.id,
    handle: data.handle,
    title: data.title,
    url: data.url || `/products/${data.handle}`,
    image: sized(data.featured_image),
    single: variants.length === 1 && variants[0].title === 'Default Title',
    variants,
  };
}

function detailFrom(element) {
  const data = element.dataset;
  return {
    variantId: data.variantId || '',
    productId: data.productId || '',
    handle: data.productHandle || '',
    productTitle: data.productTitle || '',
    variantTitle: data.variantTitle || '',
    image: data.image || '',
    url: data.url || '',
    source: data.source || '',
  };
}

class LucidBisSheet extends HTMLElement {
  #controller = null;
  #product = null;
  #selected = new Set();
  #detail = {};
  #opener = null;
  #busy = false;
  #subscribed = false;
  #request = 0;
  #i18n = {};

  connectedCallback() {
    if (active && active !== this && active.isConnected) {
      this.remove();
      return;
    }
    active = this;
    const i18n = readJSON(this.querySelector('[data-bis-i18n]')) || {};
    this.#i18n = Object.fromEntries(Object.entries(i18n).map(([key, value]) => [key, decode(String(value))]));
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener('click', this.#onDocumentClick, { signal });
    const off = on('bis:open', (event) => this.open(event.detail || {}));
    signal.addEventListener('abort', off);

    const dialog = this.dialog;
    dialog?.addEventListener('click', this.#onDialogClick, { signal });
    dialog?.addEventListener('close', this.#onClose, { signal });
    this.querySelector('[data-bis-form]')?.addEventListener('submit', this.#onSubmit, { signal });
    this.querySelector('[data-bis-sizes]')?.addEventListener('click', this.#onChip, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.dialog) unlockScroll(this.dialog);
    if (active === this) active = null;
  }

  get dialog() {
    const dialog = this.querySelector('[data-bis-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  #el(name) {
    return this.querySelector(`[data-bis-${name}]`);
  }

  async open(detail = {}, opener = null) {
    const dialog = this.dialog;
    if (!dialog) return;
    this.#detail = detail;
    this.#opener = opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    this.#subscribed = false;
    this.#reset();

    let product = pageProduct(detail);
    this.#renderHead(product, detail);
    this.#renderSizes(product, true);

    if (!dialog.open) {
      dialog.showModal();
      lockScroll(dialog);
    }
    this.#focusStart();
    emit('bis:opened', { productId: String(product?.id || detail.productId || ''), variantId: String(detail.variantId || ''), source: detail.source || '' });

    if (!product) {
      const request = ++this.#request;
      product = await this.#fetchProduct(detail);
      if (request !== this.#request || !dialog.open) return;
      this.#renderHead(product, detail);
    }
    this.#product = product || this.#fallbackProduct(detail);
    this.#renderSizes(this.#product, false);
  }

  close() {
    const dialog = this.dialog;
    if (dialog?.open) dialog.close();
  }

  async #fetchProduct({ handle, url }) {
    const root = this.dataset.root || '/';
    let path = '';
    if (handle) path = `${root}products/${encodeURIComponent(handle)}.js`;
    else if (url) path = `${url.split('?')[0].replace(/\/$/, '')}.js`;
    if (!path) return null;
    try {
      const response = await fetch(path, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return fromAjax(await response.json());
    } catch {
      return null;
    }
  }

  #fallbackProduct(detail) {
    if (!detail.variantId) return null;
    return {
      id: detail.productId || '',
      handle: detail.handle || '',
      title: detail.productTitle || '',
      url: detail.url || '',
      image: detail.image || '',
      single: !detail.variantTitle,
      variants: [{ id: detail.variantId, title: detail.variantTitle || '', available: false }],
    };
  }

  #renderHead(product, detail) {
    const name = this.#el('name');
    const thumb = this.#el('thumb');
    const variant = product?.variants?.find((item) => String(item.id) === String(detail.variantId));
    const title = product?.title || detail.productTitle || '';
    const src = variant?.image || product?.image || sized(detail.image);
    if (name) name.textContent = title;
    if (thumb) {
      let image = thumb.querySelector('img');
      if (src && !image) {
        image = document.createElement('img');
        Object.assign(image, { width: 64, height: 80, loading: 'eager', decoding: 'async' });
        thumb.append(image);
      }
      if (image && src) {
        image.src = src;
        image.alt = '';
      }
      if (image) image.hidden = !src;
    }
    const link = this.#el('product-link');
    const url = product?.url || detail.url;
    if (link instanceof HTMLAnchorElement && url) link.href = url;
  }

  #renderSizes(product, loading) {
    const wrap = this.#el('sizes-wrap');
    const chips = this.#el('sizes');
    const hint = this.#el('sizes-hint');
    const intro = this.#el('intro');
    const fields = this.#el('fields');
    const available = this.#el('available');
    if (!wrap || !chips) return;

    if (loading && !product) {
      wrap.hidden = !(this.#detail.variantTitle || this.#detail.handle);
      chips.setAttribute('aria-busy', 'true');
      chips.replaceChildren();
      if (this.#detail.variantId && this.#detail.variantTitle) {
        chips.append(this.#chip({ id: this.#detail.variantId, title: this.#detail.variantTitle }, true));
        this.#selected = new Set([String(this.#detail.variantId)]);
      }
      return;
    }

    chips.setAttribute('aria-busy', 'false');
    chips.removeAttribute('aria-label');
    const soldOut = (product?.variants || []).filter((variant) => !variant.available);
    const single = Boolean(product?.single) || (soldOut.length === 1 && !soldOut[0].title);

    if (!soldOut.length && product) {
      wrap.hidden = true;
      if (fields) fields.hidden = true;
      if (available) available.hidden = false;
      return;
    }
    if (fields) fields.hidden = false;
    if (available) available.hidden = true;

    const wanted = String(this.#detail.variantId || '');
    let selected = soldOut.filter((variant) => String(variant.id) === wanted);
    if (!selected.length && soldOut.length === 1) selected = soldOut;
    this.#selected = new Set(selected.map((variant) => String(variant.id)));

    if (intro) intro.textContent = single ? this.#i18n.introSingle || intro.textContent : this.#i18n.intro || intro.textContent;

    if (single) {
      wrap.hidden = true;
      this.#selected = new Set(soldOut.map((variant) => String(variant.id)));
      chips.replaceChildren();
      return;
    }

    wrap.hidden = false;
    chips.replaceChildren(...soldOut.map((variant) => this.#chip(variant, this.#selected.has(String(variant.id)))));
    if (hint) hint.hidden = soldOut.length < 2;
  }

  #chip(variant, pressed) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'lucid-bis-sheet__chip lucid-focus';
    button.dataset.variantId = String(variant.id);
    button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    button.textContent = variant.title;
    return button;
  }

  #onChip = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('.lucid-bis-sheet__chip') : null;
    if (!(chip instanceof HTMLButtonElement)) return;
    const id = chip.dataset.variantId || '';
    const pressed = chip.getAttribute('aria-pressed') !== 'true';
    chip.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    if (pressed) this.#selected.add(id);
    else this.#selected.delete(id);
    if (this.#selected.size) this.#status('', '');
  };

  #focusStart() {
    const email = this.#el('email');
    if (!(email instanceof HTMLInputElement)) return;
    if (!email.value && this.dataset.email) email.value = this.dataset.email;
    if (window.matchMedia('(pointer: fine)').matches && !email.value) email.focus({ preventScroll: true });
  }

  #reset() {
    const form = this.#el('form');
    const done = this.#el('done');
    if (form) form.hidden = false;
    const intro = this.#el('intro');
    if (intro) intro.hidden = false;
    if (done) done.hidden = true;
    const trap = this.#el('trap');
    if (trap instanceof HTMLInputElement) trap.value = '';
    const newsletter = this.#el('newsletter');
    if (newsletter instanceof HTMLInputElement) newsletter.checked = false;
    this.#status('', '');
    this.#setBusy(false);
    this.#product = null;
    this.#selected = new Set();
  }

  #status(state, text) {
    const status = this.#el('status');
    if (!status) return;
    status.textContent = text;
    status.dataset.state = state;
    const email = this.#el('email');
    if (email) email.toggleAttribute('aria-invalid', state === 'invalid');
  }

  #setBusy(busy) {
    this.#busy = busy;
    const submit = this.#el('submit');
    if (!(submit instanceof HTMLButtonElement)) return;
    submit.toggleAttribute('aria-busy', busy);
    submit.disabled = busy;
  }

  #onDocumentClick = (event) => {
    if (event.defaultPrevented) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-bis-close]') && this.contains(target)) {
      event.preventDefault();
      this.close();
      return;
    }
    const opener = target.closest('[data-lucid-bis-open]');
    if (!(opener instanceof HTMLElement) || this.contains(opener)) return;
    event.preventDefault();
    this.open(detailFrom(opener), opener);
  };

  #onDialogClick = (event) => {
    const dialog = this.dialog;
    if (!dialog || event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) this.close();
  };

  #onClose = () => {
    const dialog = this.dialog;
    if (dialog) unlockScroll(dialog);
    this.#request += 1;
    emit('bis:closed', { productId: String(this.#product?.id || this.#detail.productId || ''), subscribed: this.#subscribed });
    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  };

  #headers() {
    return { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: REVISION };
  }

  #post(path, body) {
    const company = encodeURIComponent(this.dataset.companyId || '');
    return fetch(`${API}/${path}/?company_id=${company}`, {
      method: 'POST',
      headers: this.#headers(),
      body: JSON.stringify(body),
    });
  }

  #done() {
    return session.getJSON(DONE_KEY, []) || [];
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy) return;

    const emailInput = this.#el('email');
    const email = emailInput instanceof HTMLInputElement ? emailInput.value.trim().toLowerCase() : '';
    if (!email || !EMAIL.test(email) || (emailInput instanceof HTMLInputElement && !emailInput.checkValidity())) {
      this.#status('invalid', this.#i18n.invalid || '');
      emailInput?.focus();
      return;
    }
    if (!this.#product && !this.#selected.size) {
      this.#status('error', this.#i18n.sizeRequired || '');
      return;
    }
    const ids = Array.from(this.#selected);
    if (!ids.length) {
      this.#status('error', this.#i18n.sizeRequired || '');
      this.querySelector('.lucid-bis-sheet__chip')?.focus();
      return;
    }

    const trap = this.#el('trap');
    if (trap instanceof HTMLInputElement && trap.value) {
      this.#showDone(email, ids);
      return;
    }

    const done = this.#done();
    const fresh = ids.filter((id) => !done.includes(`${email}|${id}`));
    if (!fresh.length) {
      this.#status('success', this.#i18n.cooldown || '');
      return;
    }

    this.#setBusy(true);
    this.#status('', '');
    const language = (this.dataset.language || 'de').slice(0, 2);
    const productTitle = this.#product?.title || this.#detail.productTitle || '';

    try {
      await this.#post('profiles', {
        data: {
          type: 'profile',
          attributes: {
            email,
            locale: this.dataset.locale || language,
            properties: {
              lucid_language: language,
              lucid_shop_root: this.dataset.shopRoot || '',
              lucid_bis_last_product: productTitle,
            },
          },
        },
      }).catch(() => null);

      const results = await Promise.allSettled(
        fresh.map((id) =>
          this.#post('back-in-stock-subscriptions', {
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email } } },
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${id}` } },
              },
            },
          }).then((response) => {
            if (!response.ok) throw new Error(String(response.status));
            return id;
          })
        )
      );
      const ok = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      if (!ok.length) throw new Error('rejected');

      const newsletter = this.#el('newsletter');
      const wantsNewsletter = newsletter instanceof HTMLInputElement && newsletter.checked && Boolean(this.dataset.listId);
      if (wantsNewsletter) {
        await this.#post('subscriptions', {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Back in stock sheet',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: this.dataset.listId } } },
          },
        }).catch(() => null);
      }

      session.setJSON(DONE_KEY, [...done, ...ok.map((id) => `${email}|${id}`)].slice(-50));
      this.#subscribed = true;
      emit('bis:subscribed', {
        productId: String(this.#product?.id || this.#detail.productId || ''),
        variantIds: ok,
        source: this.#detail.source || '',
        newsletter: wantsNewsletter,
      });
      this.#showDone(email, ok);
    } catch {
      this.#status('error', this.#i18n.error || '');
    } finally {
      this.#setBusy(false);
    }
  };

  #showDone(email, ids) {
    const titles = ids
      .map((id) => this.#product?.variants?.find((variant) => String(variant.id) === String(id))?.title || '')
      .filter((title) => title && title !== 'Default Title');
    const size = titles.length ? titles.join(', ') : this.#product?.title || this.#detail.productTitle || '';
    const text = (this.#i18n.doneText || '').replace('[email]', email).replace('[size]', size);
    const form = this.#el('form');
    const done = this.#el('done');
    const doneText = this.#el('done-text');
    if (doneText) doneText.textContent = text;
    const intro = this.#el('intro');
    if (intro) intro.hidden = true;
    if (form) form.hidden = true;
    if (done) {
      done.hidden = false;
      done.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('lucid-bis-sheet')) customElements.define('lucid-bis-sheet', LucidBisSheet);
